import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import add from '@iconify/icons-ic/add';
import { BiSync, BiCloudUpload } from 'react-icons/bi';
import { MdAssignmentInd } from 'react-icons/md';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import {
  Box,
  Tooltip,
  IconButton,
  Typography,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { RootStyle, SearchStyle, DateRangeStyled } from '../Global/Styling';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { fDate } from '../../utils/formatTime';
import useProspectSelector from '../../store/Slice/Prospect/ProspectSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getCityData,
  getInternalSaleManager,
  getSourceListData
} from '../../store/Slice/Global/GlobalSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getProspectList } from '../../store/Slice/Prospect/ProspectSlice';

export default function ProspectToolbar({
  filterName,
  onFilterName,
  parent,
  selectStatus,
  onFilterStatus,
  selectSource,
  onFilterSource,
  filterDatesRange,
  setFilterDatesRange,
  setOpenAssign,
  onAssignSubmit,
  openAssign,
  selected,
  alertMsg,
  setAlertMsg,
  setAssignValue,
  assignValue,
  setSelectSource,
  setFilterName,
  setSelectStatus,
  setCityListData
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { ProspectListDrop } = useProspectSelector();
  const { SourceDrop, InternalSaleManagerFilterDrop } = useGlobalSelector();
  const [ui, setUi] = useState({
    isOpenFilter: false,
    openSyncLeads: false,
    sourceValue: 'facebook'
  });

  useEffect(() => {
    dispatch(getProspectList({ stageId: 56 }));
    dispatch(getSourceListData());
    dispatch(getCityData({ stateId: null }));
    if (LoginData?.companyId) dispatch(getInternalSaleManager({ companyId: LoginData.companyId }));
  }, [dispatch, LoginData]);

  const resetFilters = useCallback(() => {
    setFilterDatesRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setSelectSource(' ');
    setCityListData(null);
    setSelectStatus(' ');
    setFilterName('');
  }, [setFilterDatesRange, setSelectSource, setCityListData, setSelectStatus, setFilterName]);

  const renderMenuItems = (list) =>
    list?.map((item, idx) => (
      <MenuItem key={idx} value={item.value}>
        {item.label}
      </MenuItem>
    ));

  return (
    <>
      {ui.isOpenFilter && (
        <Box px={3} pt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                inputProps={{ className: `textInput customSelectBox` }}
                value={selectSource}
                onChange={onFilterSource}
              >
                <MenuItem value=" " disabled>
                  Select Source
                </MenuItem>
                {renderMenuItems(SourceDrop)}
              </Select>
            </Grid>
            <Grid item xs={12} md={5} sx={{ position: 'relative' }}>
              <Box className="displayDateRange">
                {filterDatesRange[0].startDate ? (
                  `${fDate(filterDatesRange[0].startDate)} to ${fDate(filterDatesRange[0].endDate)}`
                ) : (
                  <Typography className="dissabledMenu">Select Date Range</Typography>
                )}
              </Box>
              <Select
                fullWidth
                inputProps={{ className: 'textInput' }}
                value=" "
                className="datesSelect"
              >
                <DateRangeStyled
                  editableDateInputs={false}
                  onChange={(item) => setFilterDatesRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={filterDatesRange}
                  className="dateRangePicker"
                />
              </Select>
            </Grid>
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                inputProps={{ className: `textInput customSelectBox` }}
                value={selectStatus}
                onChange={onFilterStatus}
              >
                <MenuItem value=" " disabled>
                  Select Status
                </MenuItem>
                {renderMenuItems(ProspectListDrop)}
              </Select>
            </Grid>
            <Grid
              item
              xs={12}
              md={1}
              display="flex"
              justifyContent={{ xs: 'center', md: 'flex-start' }}
            >
              <Tooltip title="Reset Filter">
                <IconButton onClick={resetFilters} size="small">
                  <FilterListOffIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}

      <RootStyle
        sx={{
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          p: { xs: 2, sm: 2.5 },
          bgcolor: '#ffffff'
        }}
      >
        <Box display="flex" gap={1.25} flexWrap="wrap" alignItems="center">
          <Button
            variant="contained"
            onClick={() => navigate(`/${parent}/add`)}
            startIcon={<Icon icon={add} />}
            sx={{
              bgcolor: '#0284c7',
              color: '#ffffff',
              borderRadius: '10px',
              px: 2.2,
              py: 0.9,
              fontWeight: 600,
              fontSize: '13px',
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(2, 132, 199, 0.25)',
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            + New Prospect
          </Button>

          <Button
            variant="outlined"
            onClick={() => navigate(`/${parent}/upload`)}
            startIcon={<BiCloudUpload size={18} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: '#475569',
              borderColor: '#e2e8f0',
              bgcolor: '#ffffff',
              px: 1.75,
              py: 0.8,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.04)',
                color: '#0284c7'
              }
            }}
          >
            Upload
          </Button>

          <Button
            variant="outlined"
            onClick={() => setUi((p) => ({ ...p, openSyncLeads: true }))}
            startIcon={<BiSync size={18} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: '#475569',
              borderColor: '#e2e8f0',
              bgcolor: '#ffffff',
              px: 1.75,
              py: 0.8,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.04)',
                color: '#0284c7'
              }
            }}
          >
            Sync Leads
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              setOpenAssign(true);
              setAlertMsg(false);
            }}
            startIcon={<MdAssignmentInd size={18} />}
            disabled={!selected?.length}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: '#475569',
              borderColor: '#e2e8f0',
              bgcolor: '#ffffff',
              px: 1.75,
              py: 0.8,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.04)',
                color: '#0284c7'
              }
            }}
          >
            Assign ({selected?.length || 0})
          </Button>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Prospect..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={searchFill} style={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#0284c7' }
              }
            }}
          />

          <Button
            variant="outlined"
            onClick={() => setUi((p) => ({ ...p, isOpenFilter: !p.isOpenFilter }))}
            startIcon={<Icon icon={roundFilterList} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: ui.isOpenFilter ? '#0284c7' : '#475569',
              borderColor: ui.isOpenFilter ? '#0284c7' : '#e2e8f0',
              bgcolor: ui.isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#fff',
              height: 40,
              px: 2,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.06)',
                color: '#0284c7'
              }
            }}
          >
            Filters
          </Button>
        </Box>
      </RootStyle>

      <Dialog
        open={ui.openSyncLeads}
        onClose={() => setUi((p) => ({ ...p, openSyncLeads: false }))}
        fullWidth
      >
        <DialogTitle className="popupTitle">
          <Typography variant="h6">Select Source</Typography>
          <IconButton onClick={() => setUi((p) => ({ ...p, openSyncLeads: false }))} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <RadioGroup
            value={ui.sourceValue}
            onChange={(e) => setUi((p) => ({ ...p, sourceValue: e.target.value }))}
          >
            <FormControlLabel value="facebook" control={<Radio />} label="Facebook" />
            <FormControlLabel value="callCentre" control={<Radio />} label="Call Centre" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUi((p) => ({ ...p, openSyncLeads: false }))}
            variant="contained"
            className="mainBtn"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAssign} fullWidth>
        <DialogTitle className="popupTitle">
          <Typography variant="h6">{selected?.length} Prospect Selected</Typography>
          <IconButton
            onClick={() => {
              setOpenAssign(false);
              setAlertMsg(false);
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Select
            fullWidth
            inputProps={{ className: `textInput customSelectBox` }}
            value={assignValue}
            onChange={(e) => {
              setAlertMsg(false);
              setAssignValue(e.target.value);
            }}
          >
            <MenuItem value=" " disabled>
              Select Agent Name
            </MenuItem>
            {renderMenuItems(InternalSaleManagerFilterDrop)}
          </Select>
          {alertMsg && <FormHelperText error>Please select agent name</FormHelperText>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onAssignSubmit} variant="contained" className="mainBtn">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
